/**
 * Local persistence — deliberately small, and every key purged on sign-out
 * (docs/08 §2.4: no authenticated data survives a sign-out, and the service
 * worker gets a purge message too).
 *
 * The cached feed exists so a driver in a parking structure with no signal
 * still sees their last answer — WITH a visible age stamp, never silently.
 *
 * Coordinates stored here are COARSE (3 decimals ≈ 100 m): enough to resume
 * a search, not enough to be a location history. Saved places are explicit
 * user content ("Home", "Airport"), not telemetry.
 */
import type { Feed } from './api.js';

const KEYS = {
  feed: 'surge.lastFeed',
  attempt: 'surge.attempt',
  mode: 'surge.mode',
  origin: 'surge.lastOrigin',
  search: 'surge.search',
  places: 'surge.places',
  prefs: 'surge.prefs',
  flags: 'surge.flags',
} as const;

/** The active plan: enough to draw the TripCard offline and target Maps. */
export interface StoredAttempt {
  id: string;
  occurrence_id: string;
  title: string;          // venue (or event title when no venue)
  subtitle: string;       // event title
  staging_label: string;
  staging_approach: string | null;
  staging_lat: number;
  staging_lng: number;
  leave_by: string | null;
  p10_at: string;
  p90_at: string;
  expires_at: string;
}

export interface SavedPlace {
  id: string;
  label: string;
  detail: string | null;
  lat: number;
  lng: number;
}

/** Where to search: the driver's live position, or a chosen reference point. */
export type SearchCenter =
  | { kind: 'gps' }
  | { kind: 'place'; place: SavedPlace };

export const RADIUS_STEPS_MI = [5, 10, 15, 25] as const;
export type RadiusMi = typeof RADIUS_STEPS_MI[number];
/** Metro-wide by default (docs/10 S3); the chip only mentions a radius when narrowed. */
export const DEFAULT_RADIUS_MI: RadiusMi = 25;

export interface SearchContext {
  center: SearchCenter;
  radiusMi: RadiusMi;
}
export const DEFAULT_SEARCH: SearchContext = { center: { kind: 'gps' }, radiusMi: DEFAULT_RADIUS_MI };
export const MAX_SAVED_PLACES = 5;

export type MapsProvider = 'apple' | 'google' | 'waze';
export interface Prefs {
  maps: MapsProvider;
  units: 'mi' | 'km';
  reminders: boolean;
}
const IS_APPLE = /iPhone|iPad|Macintosh/.test(navigator.userAgent);
export const DEFAULT_PREFS: Prefs = { maps: IS_APPLE ? 'apple' : 'google', units: 'mi', reminders: true };

export interface Flags {
  /** The one-time profile sheet has been shown (answered or skipped). */
  profilePrompted?: boolean;
  /** Last metro chosen on the Welcome screen, for the preview. */
  welcomeMetro?: string;
}

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) as T : null;
  } catch { return null; }
}
function write(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* full/blocked */ }
}

export function coarse(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function isIso(v: unknown): v is string {
  return typeof v === 'string' && Number.isFinite(new Date(v).getTime());
}

export const store = {
  feed: {
    /** A feed cached by an older build lacks fields newer screens draw
     *  (the crowd meter); treat it as no cache rather than crash on it. */
    get: (): { at: number; feed: Feed } | null => {
      const c = read<{ at: number; feed: Feed }>(KEYS.feed);
      const ok = c && typeof c.at === 'number' && Array.isArray(c.feed?.opportunities)
        && c.feed.opportunities.every((o) => typeof o?.crowd?.level === 'number' && 'exit_reports' in o
          && typeof o.occurrence?.source === 'string');
      if (c && !ok) localStorage.removeItem(KEYS.feed);
      return ok ? c : null;
    },
    set: (feed: Feed) => write(KEYS.feed, { at: Date.now(), feed }),
  },
  attempt: {
    /** A trip saved by an older build lacks the window fields the TripCard
     *  needs; treat it as no trip rather than crash the shell on it. */
    get: (): StoredAttempt | null => {
      const a = read<Partial<StoredAttempt>>(KEYS.attempt);
      if (!a || typeof a.id !== 'string' || !isIso(a.p10_at) || !isIso(a.p90_at) || !isIso(a.expires_at)) {
        if (a) localStorage.removeItem(KEYS.attempt);
        return null;
      }
      return a as StoredAttempt;
    },
    set: (a: StoredAttempt) => write(KEYS.attempt, a),
    clear: () => localStorage.removeItem(KEYS.attempt),
  },
  mode: {
    get: () => read<string>(KEYS.mode),
    set: (m: string) => write(KEYS.mode, m),
  },
  origin: {
    get: () => read<{ lat: number; lng: number }>(KEYS.origin),
    set: (o: { lat: number; lng: number }) =>
      write(KEYS.origin, { lat: coarse(o.lat), lng: coarse(o.lng) }),
  },
  search: {
    get: (): SearchContext => {
      const s = read<SearchContext>(KEYS.search);
      if (!s) return DEFAULT_SEARCH;
      // Older builds stored slider values (1–30); snap to the four steps.
      const r = (RADIUS_STEPS_MI as readonly number[]).includes(s.radiusMi) ? s.radiusMi : DEFAULT_RADIUS_MI;
      const c = s.center;
      const validPlace = c?.kind === 'place' && c.place
        && typeof c.place.lat === 'number' && typeof c.place.lng === 'number' && typeof c.place.label === 'string';
      return { center: validPlace ? c : { kind: 'gps' }, radiusMi: r as RadiusMi };
    },
    set: (s: SearchContext) => write(KEYS.search, s),
  },
  places: {
    get: () => read<SavedPlace[]>(KEYS.places) ?? [],
    set: (p: SavedPlace[]) => write(KEYS.places, p.slice(0, MAX_SAVED_PLACES)),
  },
  prefs: {
    get: (): Prefs => ({ ...DEFAULT_PREFS, ...(read<Partial<Prefs>>(KEYS.prefs) ?? {}) }),
    set: (p: Prefs) => write(KEYS.prefs, p),
  },
  flags: {
    get: (): Flags => read<Flags>(KEYS.flags) ?? {},
    set: (f: Flags) => write(KEYS.flags, f),
  },

  purgeAll(): void {
    // Flags hold no personal data (a UI choice and a "seen it" bit); keeping
    // them means a sign-out does not re-ask the one-time questions.
    for (const k of Object.values(KEYS)) if (k !== KEYS.flags) localStorage.removeItem(k);
    navigator.serviceWorker?.controller?.postMessage('purge');
  },
};
